import { createReducer, on } from "@ngrx/store";
import { CurrentCallState, CallEntry, CallSession, CallLeg, LegStatus, SessionStatus } from "./current-call-state";
import { CurrentCallActions } from "./current-call.actions";

export const initialCallEntry: CallEntry = {
  callId: null,
  installationId: null,
  entityId: null,
  entityKind: null,
  calleeId: null,
  calleeKind: null,
  destinationId: null,
  destinationKind: null,
  error: null
};

export const initialState: CurrentCallState = {
  calls: [],
  activeCallId: null
};

/** Helper: update a specific CallEntry in the array by callId */
function updateCall(
  state: CurrentCallState,
  callId: string,
  updater: (entry: CallEntry) => CallEntry
): CurrentCallState {
  const idx = state.calls.findIndex(c => c.callId === callId);
  if (idx === -1) return state;
  const updated = [...state.calls];
  updated[idx] = updater(updated[idx]);
  return { ...state, calls: updated };
}

export const currentCallReducer = createReducer(
  initialState,

  // Start a new call - push into array (replace if same callId exists)
  on(CurrentCallActions.startCall, (state, { callId, installationId, callee, incoming, originalCallee, destination }) => {
    const newEntry: CallEntry = {
      ...initialCallEntry,
      callId,
      installationId,
      entityId: callee.id,
      entityKind: callee.kind,
      calleeId: originalCallee?.id ?? null,
      calleeKind: originalCallee?.kind ?? null,
      destinationId: destination?.id ?? null,
      destinationKind: destination?.kind ?? null,
      mainLeg: {
        sessions: [],
        entity: { id: callee.id, kind: callee.kind },
        entityName: callee.name ?? '',
        entityOutputs: callee.outputs ?? [],
        entityThingName: callee.thingName,
        entityIsV2: callee.isV2,
        status: (incoming ? 'incoming' : 'outgoing') as LegStatus
      }
    };

    // Replace if same callId exists, otherwise push
    const existingIdx = state.calls.findIndex(c => c.callId === callId);
    const calls = existingIdx !== -1
      ? state.calls.map((c, i) => i === existingIdx ? newEntry : c)
      : [...state.calls, newEntry];

    // Set activeCallId only if there's no active call yet
    const activeCallId = state.activeCallId ?? callId;

    return { ...state, calls, activeCallId };
  }),

  // A new session was created
  on(CurrentCallActions.sessionStarted, (state, { callId, session }) => {
    return updateCall(state, callId, (entry) => {
      // If no mainLeg exists, create it
      if (!entry.mainLeg) {
        return {
          ...entry,
          mainLeg: {
            sessions: [session],
            entity: session.entity ?? { id: entry.entityId!, kind: entry.entityKind! },
            entityName: session.entityName ?? '',
            entityOutputs: session.entityOutputs ?? [],
            entityThingName: session.entityThingName,
            status: 'outgoing' as LegStatus
          }
        };
      }

      // Add session to existing mainLeg
      return {
        ...entry,
        mainLeg: {
          ...entry.mainLeg,
          sessions: [...entry.mainLeg.sessions, session],
          entity: entry.mainLeg.entityName ? entry.mainLeg.entity : (session.entity ?? entry.mainLeg.entity),
          entityName: entry.mainLeg.entityName || session.entityName || '',
          entityOutputs: entry.mainLeg.entityOutputs?.length ? entry.mainLeg.entityOutputs : (session.entityOutputs ?? []),
          entityThingName: entry.mainLeg.entityThingName ?? session.entityThingName
        }
      };
    });
  }),

  // A session was answered - becomes active
  on(CurrentCallActions.sessionAnswered, (state, { callId, sessionId }) => {
    return updateCall(state, callId, (entry) => {
      // Check pipLeg
      if (entry.pipLeg?.sessions.some(s => s.sessionId === sessionId)) {
        const updatedSessions = entry.pipLeg.sessions.map(s =>
          s.sessionId === sessionId ? { ...s, status: 'answered' as SessionStatus } : s
        );
        return {
          ...entry,
          pipLeg: { ...entry.pipLeg, sessions: updatedSessions, status: 'speaking' as LegStatus }
        };
      }

      // Check mainLeg
      if (entry.mainLeg?.sessions.some(s => s.sessionId === sessionId)) {
        const updatedSessions = entry.mainLeg.sessions.map(s =>
          s.sessionId === sessionId ? { ...s, status: 'answered' as SessionStatus } : s
        );
        return {
          ...entry,
          mainLeg: { ...entry.mainLeg, sessions: updatedSessions, status: 'speaking' as LegStatus }
        };
      }

      return entry;
    });
  }),

  // Put current active session on hold - move mainLeg to pipLeg
  on(CurrentCallActions.hold, (state, { callId }) => {
    return updateCall(state, callId, (entry) => {
      if (!entry.mainLeg || entry.mainLeg.status !== 'speaking') return entry;

      const updatedSessions = entry.mainLeg.sessions.map(s =>
        s.status === 'answered' ? { ...s, status: 'hold' as SessionStatus } : s
      );

      return {
        ...entry,
        mainLeg: undefined,
        pipLeg: { ...entry.mainLeg, sessions: updatedSessions, status: 'hold' as LegStatus }
      };
    });
  }),

  // Resume the held session - move pipLeg back to mainLeg
  on(CurrentCallActions.resume, (state, { callId }) => {
    return updateCall(state, callId, (entry) => {
      if (!entry.pipLeg || entry.pipLeg.status !== 'hold') return entry;

      const updatedSessions = entry.pipLeg.sessions.map(s =>
        s.status === 'hold' ? { ...s, status: 'answered' as SessionStatus } : s
      );

      return {
        ...entry,
        mainLeg: { ...entry.pipLeg, sessions: updatedSessions, status: 'speaking' as LegStatus },
        pipLeg: undefined
      };
    });
  }),

  // Start forwarding (while on hold) - create mainLeg in outgoing state immediately
  on(CurrentCallActions.startForward, (state, { callId, callee }) => {
    return updateCall(state, callId, (entry) => {
      if (!entry.pipLeg || entry.pipLeg.status !== 'hold' || entry.mainLeg) return entry;

      return {
        ...entry,
        mainLeg: {
          sessions: [],
          entity: { id: callee.id, kind: callee.kind },
          entityName: callee.name ?? '',
          entityOutputs: callee.outputs ?? [],
          entityThingName: callee.thingName,
          entityIsV2: callee.isV2,
          status: 'outgoing' as LegStatus
        }
      };
    });
  }),

  // A session ended
  on(CurrentCallActions.sessionEnded, (state, { callId, sessionId }) => {
    return updateCall(state, callId, (entry) => {
      // Check mainLeg first
      if (entry.mainLeg?.sessions.some(s => s.sessionId === sessionId)) {
        const remaining = entry.mainLeg.sessions.filter(s => s.sessionId !== sessionId);

        if (remaining.length === 0) {
          if (entry.pipLeg) {
            // pipLeg exists (on hold) - just clear mainLeg, keep pipLeg in hold
            return { ...entry, mainLeg: undefined };
          }
          // No pipLeg - this call entry is done, will be cleaned up by reset
          return { ...entry, mainLeg: undefined };
        }

        return { ...entry, mainLeg: { ...entry.mainLeg, sessions: remaining } };
      }

      // Check pipLeg
      if (entry.pipLeg?.sessions.some(s => s.sessionId === sessionId)) {
        const remaining = entry.pipLeg.sessions.filter(s => s.sessionId !== sessionId);

        if (remaining.length === 0) {
          if (entry.mainLeg) {
            return { ...entry, pipLeg: undefined };
          }
          // No mainLeg either - this call entry is done
          return { ...entry, pipLeg: undefined };
        }

        return { ...entry, pipLeg: { ...entry.pipLeg, sessions: remaining } };
      }

      return entry;
    });
  }),

  // Bridge: set both legs to 'bridging' state
  on(CurrentCallActions.bridge, (state, { callId }) => {
    return updateCall(state, callId, (entry) => {
      if (!entry.mainLeg || !entry.pipLeg) return entry;

      return {
        ...entry,
        mainLeg: { ...entry.mainLeg, status: 'bridging' as LegStatus },
        pipLeg: { ...entry.pipLeg, status: 'bridging' as LegStatus }
      };
    });
  }),

  // Clear mainLeg while keeping pipLeg on hold (e.g. cancel forward)
  on(CurrentCallActions.clearMainLeg, (state, { callId }) => {
    return updateCall(state, callId, (entry) => ({
      ...entry,
      mainLeg: undefined
    }));
  }),

  // Call error occurred
  on(CurrentCallActions.callError, (state, { callId, error }) => {
    return updateCall(state, callId, (entry) => ({
      ...entry,
      error
    }));
  }),

  // Reset a specific call (remove from array)
  on(CurrentCallActions.reset, (state, { callId }) => {
    const calls = state.calls.filter(c => c.callId !== callId);
    let activeCallId = state.activeCallId;
    if (activeCallId === callId) {
      // Fallback to next call or null
      activeCallId = calls.length > 0 ? calls[0].callId : null;
    }
    return { ...state, calls, activeCallId };
  }),

  // Reset all calls to idle state
  on(CurrentCallActions.resetAll, () => ({ ...initialState })),

  // Set the active call
  on(CurrentCallActions.setActiveCall, (state, { callId }) => ({
    ...state,
    activeCallId: callId
  })),

  // Update session entity info (name, outputs, thingName)
  on(CurrentCallActions.updateSessionEntityInfo, (state, { callId, sessionId, entityName, entityOutputs, entityThingName, entityIsV2 }) => {
    return updateCall(state, callId, (entry) => {
      // Check mainLeg
      if (entry.mainLeg?.sessions.some(s => s.sessionId === sessionId)) {
        const updatedSessions = entry.mainLeg.sessions.map(s =>
          s.sessionId === sessionId
            ? {
              ...s,
              entityName: entityName ?? s.entityName,
              entityOutputs: entityOutputs ?? s.entityOutputs,
              entityThingName: entityThingName ?? s.entityThingName
            }
            : s
        );
        return {
          ...entry,
          mainLeg: {
            ...entry.mainLeg,
            sessions: updatedSessions,
            entityName: entityName ?? entry.mainLeg.entityName,
            entityOutputs: entityOutputs ?? entry.mainLeg.entityOutputs,
            entityThingName: entityThingName ?? entry.mainLeg.entityThingName,
            entityIsV2: entityIsV2 ?? entry.mainLeg.entityIsV2
          }
        };
      }

      // Check pipLeg
      if (entry.pipLeg?.sessions.some(s => s.sessionId === sessionId)) {
        const updatedSessions = entry.pipLeg.sessions.map(s =>
          s.sessionId === sessionId
            ? {
              ...s,
              entityName: entityName ?? s.entityName,
              entityOutputs: entityOutputs ?? s.entityOutputs,
              entityThingName: entityThingName ?? s.entityThingName
            }
            : s
        );
        return {
          ...entry,
          pipLeg: {
            ...entry.pipLeg,
            sessions: updatedSessions,
            entityName: entityName ?? entry.pipLeg.entityName,
            entityOutputs: entityOutputs ?? entry.pipLeg.entityOutputs,
            entityThingName: entityThingName ?? entry.pipLeg.entityThingName,
            entityIsV2: entityIsV2 ?? entry.pipLeg.entityIsV2
          }
        };
      }

      return entry;
    });
  })
);
